import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Camera, AlertCircle } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';

const MobileScanner = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    startScanner();
    
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      setScanning(true);
      setError(null);

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        onScanSuccess,
        onScanError
      );

      console.log('✅ QR Scanner started');
    } catch (err) {
      console.error('❌ Error starting scanner:', err);
      setError('Failed to access camera. Please grant camera permission and try again.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
        console.log('✅ QR Scanner stopped');
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  const onScanSuccess = async (decodedText, decodedResult) => {
    console.log('✅ QR Code scanned:', decodedText);
    
    // Stop scanner
    await stopScanner();
    
    // Extract session ID from URL
    try {
      const url = new URL(decodedText);
      const sessionId = url.searchParams.get('session');
      
      if (sessionId) {
        toast.success('QR Code scanned! Connecting...');
        
        // Navigate to pairing page with session ID
        navigate(`/mobile-pair?session=${sessionId}`);
      } else {
        setError('Invalid QR code. Please scan the pairing QR code from desktop CRM.');
        setTimeout(() => startScanner(), 2000);
      }
    } catch (err) {
      console.error('Error parsing QR code:', err);
      setError('Invalid QR code format. Please scan the correct QR code.');
      setTimeout(() => startScanner(), 2000);
    }
  };

  const onScanError = (errorMessage) => {
    // Ignore frequent scan errors
    // console.log('Scan error:', errorMessage);
  };

  const handleBack = async () => {
    await stopScanner();
    navigate('/mobile');
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="p-4 bg-slate-800 flex items-center gap-3">
        <Button 
          onClick={handleBack}
          variant="ghost"
          size="icon"
          className="text-white hover:bg-slate-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-lg font-heading tracking-tight">Scan QR Code</h1>
          <p className="text-xs text-slate-400">Point camera at desktop screen</p>
        </div>
      </div>

      {/* Scanner */}
      <div className="p-4">
        {error ? (
          <Card className="bg-red-50 border-red-200 p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-red-600 mb-3" />
            <p className="text-red-900 font-medium mb-2">Scanner Error</p>
            <p className="text-sm text-red-800 mb-4">{error}</p>
            <Button 
              onClick={() => {
                setError(null);
                startScanner();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Try Again
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Scanner Container */}
            <div className="relative">
              <div 
                id="qr-reader" 
                className="rounded-lg overflow-hidden border-4 border-blue-500"
                style={{ width: '100%' }}
              />
              
              {/* Scanning Indicator */}
              {scanning && (
                <div className="absolute top-4 left-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-3 flex items-center gap-3">
                  <Camera className="h-5 w-5 text-blue-400 animate-pulse" />
                  <div>
                    <p className="text-sm font-medium">Scanning...</p>
                    <p className="text-xs text-slate-300">Position QR code in frame</p>
                  </div>
                </div>
              )}

              {/* Corner Guides */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-4 left-4 w-8 h-8 border-t-4 border-l-4 border-blue-400"></div>
                <div className="absolute top-4 right-4 w-8 h-8 border-t-4 border-r-4 border-blue-400"></div>
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-4 border-l-4 border-blue-400"></div>
                <div className="absolute bottom-4 right-4 w-8 h-8 border-b-4 border-r-4 border-blue-400"></div>
              </div>
            </div>

            {/* Instructions */}
            <Card className="bg-slate-800 border-slate-700 p-4">
              <p className="text-sm font-medium text-white mb-2">Instructions:</p>
              <ol className="text-xs text-slate-300 space-y-1.5 list-decimal list-inside">
                <li>Hold your phone steady</li>
                <li>Position QR code within the frame</li>
                <li>Keep adequate lighting</li>
                <li>Wait for automatic detection</li>
              </ol>
            </Card>

            {/* Manual Entry Option */}
            <Card className="bg-blue-900/30 border-blue-700/50 p-4">
              <p className="text-xs text-blue-200 text-center">
                💡 Having trouble scanning? Make sure camera permissions are enabled in your browser settings.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileScanner;
